import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Container,
  Group,
  Modal,
  NumberInput,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { Link } from "wouter";
import { IconPlus } from "@tabler/icons-react";
import {
  assignPartnerMerchantCredits,
  getPartnerMerchants,
} from "../api";
import { useStore } from "../store";
import ListPending from "../_Shared/ListPending";
import Timeago from "../../../Boilerplate/_Shared/Timeago";

export default function PartnerMerchantList() {
  const authToken = useStore((state) => state.authToken);
  const [merchants, setMerchants] = useState(null);
  const [assignTo, setAssignTo] = useState(null);
  const [skuCount, setSkuCount] = useState(250);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(
    () =>
      getPartnerMerchants()
        .then((data) => setMerchants(Array.isArray(data) ? data : []))
        .catch(() => setMerchants([])),
    [],
  );

  useEffect(() => {
    refresh();
  }, [authToken, refresh]);

  return (
    <Container size="xl">
      <Group justify="space-between" align="center">
        <Title order={1}>Merchants</Title>
        <Button
          leftSection={<IconPlus size={16} />}
          variant="default"
          component={Link}
          to="/partner/merchants/create"
        >
          Create Merchant
        </Button>
      </Group>

      <ListPending items={merchants} emptyText="No linked merchants yet.">
        {(rows) => (
          <Table mt="lg" striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>How</Table.Th>
                <Table.Th ta="right">Grant remaining</Table.Th>
                <Table.Th>Bought</Table.Th>
                <Table.Th ta="right">SKUs processed</Table.Th>
                <Table.Th>Last activity</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((m) => (
                <Table.Tr key={m.tenantId}>
                  <Table.Td>{m.name}</Table.Td>
                  <Table.Td>{m.email || "-"}</Table.Td>
                  <Table.Td>{m.createdHow}</Table.Td>
                  <Table.Td ta="right">
                    <Text
                      span
                      c={
                        m.sponsoredSkuRemaining === 0 &&
                        m.sponsoredSkuGranted > 0
                          ? "orange"
                          : undefined
                      }
                    >
                      {m.sponsoredSkuRemaining}/{m.sponsoredSkuGranted}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {m.bought ? (
                      <Badge color="teal" variant="light">
                        Yes
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light">
                        No
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td ta="right">{m.skusProcessed}</Table.Td>
                  <Table.Td>
                    {m.lastActivityAt ? (
                      <Timeago date={m.lastActivityAt} />
                    ) : (
                      "-"
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Button
                      size="xs"
                      variant="subtle"
                      onClick={() => {
                        setSkuCount(250);
                        setAssignTo(m);
                      }}
                    >
                      Assign SKUs
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </ListPending>

      <Modal
        opened={!!assignTo}
        onClose={() => setAssignTo(null)}
        title={`Assign SKUs to ${assignTo?.name || ""}`}
      >
        <NumberInput
          label="SKUs"
          min={1}
          value={skuCount}
          onChange={setSkuCount}
        />
        <Button
          mt="md"
          loading={saving}
          onClick={async () => {
            setSaving(true);
            try {
              await assignPartnerMerchantCredits(assignTo.tenantId, skuCount);
              setAssignTo(null);
              refresh();
            } finally {
              setSaving(false);
            }
          }}
        >
          Transfer from pool
        </Button>
      </Modal>
    </Container>
  );
}
